import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { eventLandingGuard } from '@features/pages/event-landing/event-landing.guard';
import { OktaCallbackComponent } from '@okta/okta-angular';
import { NotFoundComponent } from './core/components/not-found/not-found.component';
import { groupLinkGuard } from './core/guards/group-link.guard';
import { logoutRedirectGuard } from './core/guards/logout-redirect.guard';
import { RedirectPageComponent } from './core/okta-integration/components/redirect-page/redirect-page.component';
import { checkoutResolver } from './features/pages/checkout/resolvers/checkout.resolver';
import { eventLandingResolver } from './features/pages/event-landing/resolvers/event-landing.resolver';
import { opponentsResolver } from './features/pages/event-landing/resolvers/opponents.resolver';
import { EventDataResolver } from './features/pages/select-seat/resolvers/event-data.resolver';
import { summaryResolver } from './features/pages/summary/resolvers/summary.resolver';

/**
 * Known first-level route segments.
 * Used to differentiate between group links and regular routes.
 */
export const KNOWN_ROUTE_SEGMENTS = [
  '',
  'okta',
  'events',
  'seat-selection',
  'login',
  'checkout',
  'payment',
  'summary',
  'not-found',
];

export const appRoutes: Routes = [
  // Shared routes — restore post-logout URL from localStorage if present
  {
    path: '',
    pathMatch: 'full',
    canActivate: [logoutRedirectGuard],
    component: NotFoundComponent,
  },
  {
    path: 'okta/redirect',
    component: RedirectPageComponent,
  },
  // Routes without group link (standard routes)
  {
    path: '',
    children: [
      {
        path: 'events',
        loadChildren: () =>
          import('./features/pages/event-landing/event-landing.module').then(
            m => m.EventLandingModule
          ),
        resolve: {
          eventList: eventLandingResolver,
          opponentList: opponentsResolver,
        },
        canActivate: [eventLandingGuard],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
      },
      {
        path: 'seat-selection',
        loadChildren: () =>
          import('./features/pages/select-seat/select-seat.module').then(
            m => m.SelectSeatModule
          ),
        resolve: {
          event: EventDataResolver,
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
      },
      {
        path: 'login',
        component: OktaCallbackComponent,
      },
      {
        path: 'checkout/:id', // <---- Transaction id
        loadChildren: () =>
          import('./features/pages/checkout/checkout.module').then(
            m => m.CheckoutModule
          ),
        resolve: {
          transaction: checkoutResolver,
        },
      },
      // The former /payment/:id route was merged into checkout/:id. Redirect
      // any lingering old URL (mid-flow OKTA return, shared link, refresh
      // during/after deploy) instead of letting it fall through to not-found.
      {
        path: 'payment/:id',
        redirectTo: 'checkout/:id',
      },
      {
        path: 'summary/:id', // <---- Transaction id
        loadChildren: () =>
          import('./features/pages/summary/summary.module').then(
            m => m.SummaryModule
          ),
        resolve: {
          transaction: summaryResolver,
        },
      },
      {
        path: 'not-found',
        component: NotFoundComponent,
      },
    ],
  },
  // Routes with group link prefix (e.g., /my-group/seat-selection)
  {
    path: ':groupLink',
    canActivate: [groupLinkGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'events',
      },
      {
        path: 'events',
        loadChildren: () =>
          import('./features/pages/event-landing/event-landing.module').then(
            m => m.EventLandingModule
          ),
        resolve: {
          eventList: eventLandingResolver,
          opponentList: opponentsResolver,
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
      },
      {
        path: 'seat-selection',
        loadChildren: () =>
          import('./features/pages/select-seat/select-seat.module').then(
            m => m.SelectSeatModule
          ),
        resolve: {
          event: EventDataResolver,
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
      },
      {
        path: 'login',
        component: OktaCallbackComponent,
      },
      {
        path: 'checkout/:id', // <---- Transaction id
        loadChildren: () =>
          import('./features/pages/checkout/checkout.module').then(
            m => m.CheckoutModule
          ),
        resolve: {
          transaction: checkoutResolver,
        },
      },
      // The former /payment/:id route was merged into checkout/:id. Redirect
      // any lingering old URL (mid-flow OKTA return, shared link, refresh
      // during/after deploy) instead of letting it fall through to not-found.
      {
        path: 'payment/:id',
        redirectTo: 'checkout/:id',
      },
      {
        path: 'summary/:id', // <---- Transaction id
        loadChildren: () =>
          import('./features/pages/summary/summary.module').then(
            m => m.SummaryModule
          ),
        resolve: {
          transaction: summaryResolver,
        },
      },
    ],
  },
  {
    path: '**', // Unknown route protection
    redirectTo: 'not-found',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
